import { FunctionComponent } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { ImageMultiSkuOverlay } from 'dibs-image-multi-sku-overlay/exports/ImageMultiSkuOverlay';
import CheckoutFormatAmount from '../shared/CheckoutFormatAmount';
import { CheckoutSummaryItemSkuDetails } from './CheckoutSummaryItemSkuDetails';
import { CheckoutMobileSummaryNewPaneTitle_transaction$key } from './__generated__/CheckoutMobileSummaryNewPaneTitle_transaction.graphql';
import { CheckoutMobileSummaryNewPaneTitle_item$key } from './__generated__/CheckoutMobileSummaryNewPaneTitle_item.graphql';
import styles from './styles/CheckoutMobileSummaryNewPaneTitle.scss';
import classnames from 'classnames';
import { useCheckoutSelector } from '../../reducers/root';

type CheckoutMobileSummaryNewPaneTitleProps = {
    isExpanded: boolean;
    transaction: CheckoutMobileSummaryNewPaneTitle_transaction$key | null;
    item: CheckoutMobileSummaryNewPaneTitle_item$key | null;
};

export const CheckoutMobileSummaryNewPaneTitle: FunctionComponent<
    CheckoutMobileSummaryNewPaneTitleProps
> = props => {
    const { isExpanded } = props;

    const offerCurrency = useSelector<$TSFixMe, string>(state => state?.transaction?.offerCurrency);

    const transaction = useFragment(
        graphql`
            fragment CheckoutMobileSummaryNewPaneTitle_transaction on Transaction {
                summary {
                    invoiceSummary {
                        buyerCurrentInvoice {
                            totalAmount {
                                ...CheckoutFormatAmount_financialAmount
                            }
                        }
                    }
                }
                sku {
                    ...ImageMultiSkuOverlay_sku
                }
                ...CheckoutSummaryItemSkuDetails_transaction
            }
        `,
        props.transaction
    );
    const item = useFragment(
        graphql`
            fragment CheckoutMobileSummaryNewPaneTitle_item on Item {
                title
                skus {
                    serviceId
                    ...ImageMultiSkuOverlay_sku
                }
                ...ImageMultiSkuOverlay_item
                ...CheckoutSummaryItemSkuDetails_item
            }
        `,
        props.item
    );
    const skuId = useCheckoutSelector(state => state.transaction.skuId);
    const sku =
        item?.skus?.find(itemSku => itemSku?.serviceId === skuId) || transaction?.sku || null;

    const totalAmount = transaction?.summary?.invoiceSummary?.buyerCurrentInvoice?.totalAmount;
    const canShowItemTitle = !!item;
    const canShowItemPrice = !!transaction;
    const summaryClassName = classnames(styles.summary, {
        [styles.shimmerGap]: !canShowItemPrice,
    });
    return (
        <div className={styles.title}>
            <div className={styles.image}>
                {item ? (
                    <ImageMultiSkuOverlay
                        item={item}
                        sku={sku}
                        messageSize="small"
                        imageAlt={item?.title || ''}
                        useSuspense
                    />
                ) : (
                    <div className={styles.shimmer} />
                )}
            </div>
            <div className={summaryClassName}>
                {canShowItemTitle ? (
                    <div className={isExpanded ? styles.itemTitleExpanded : styles.itemTitle}>
                        {item?.title}
                    </div>
                ) : (
                    <div className={classnames(styles.title, styles.shimmer)} />
                )}
                {canShowItemPrice ? (
                    !isExpanded && (
                        <div>
                            <FormattedMessage
                                id="CheckoutMobileSummaryPaneTitle.totalAmount"
                                defaultMessage="{totalAmount} Total"
                                values={{
                                    totalAmount: (
                                        <CheckoutFormatAmount
                                            financialAmount={totalAmount}
                                            currency={offerCurrency}
                                        />
                                    ),
                                }}
                            />
                        </div>
                    )
                ) : (
                    <div className={styles.priceShimmer} />
                )}
                {isExpanded && transaction && item && (
                    <CheckoutSummaryItemSkuDetails
                        item={item}
                        transaction={transaction}
                        skipItemTitle
                    />
                )}
            </div>
        </div>
    );
};
